import { Check, Circle } from 'lucide-react';
import type { ReactNode } from 'react';

import { BrandMark } from '@/components/brand/logo';
import { Card } from '@/components/ui';
import { env } from '@/lib/env';
import { PASSWORD_RULES } from '@/lib/password';
import { cn } from '@/lib/utils';

/** The centred card the signed-out screens share: sign in, forgot, reset. */
export function AuthShell({ subtitle, children }: { subtitle: string; children: ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-canvas px-4">
      <Card className="w-full max-w-sm p-6">
        <div className="mb-6 text-center">
          <BrandMark className="mx-auto size-11" />
          <h1 className="mt-3 text-lg font-bold text-ink">{env.VITE_APP_NAME}</h1>
          <p className="mt-1 text-sm text-ink-muted">{subtitle}</p>
        </div>
        {children}
      </Card>
    </main>
  );
}

/** A banner for a form-level outcome. `role` makes a screen reader announce it. */
export function FormNotice({ tone, children }: { tone: 'success' | 'danger'; children: ReactNode }) {
  return (
    <p
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'rounded-lg px-3 py-2 text-sm',
        tone === 'danger' ? 'bg-danger-50 text-danger-700' : 'bg-success-50 text-success-700',
      )}
    >
      {children}
    </p>
  );
}

/** Ticks each rule off as it is met, so a rejection never comes as a surprise. */
export function PasswordRules({ value, id }: { value: string; id?: string }) {
  return (
    <ul id={id} className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs" aria-label="Password requirements">
      {PASSWORD_RULES.map((rule) => {
        const met = rule.test(value);

        return (
          <li
            key={rule.label}
            className={cn('flex items-center gap-1.5', met ? 'text-success-700' : 'text-ink-subtle')}
          >
            {met ? (
              <Check className="size-3.5 shrink-0" aria-hidden="true" />
            ) : (
              <Circle className="size-3 shrink-0" aria-hidden="true" />
            )}
            <span>
              {rule.label}
              <span className="sr-only">{met ? ' — met' : ' — not met'}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
